import { Score, scores } from "../config/scoresConfig.js";
import axios, { AxiosError } from "axios";
import {
  Address,
  Contract,
  OpenedContract,
  SenderArguments,
  TonClient,
  Transaction,
  beginCell,
  toNano,
  // beginCell,
} from "@ton/ton";
import { UserScoreProxy } from "../config/ton/tact_UserScoreProxy.js";
import { ScoreItem } from "../config/ton/tact_ScoreItem.js";
import {
  NomisScore,
  ScoreData,
  Signature,
  storeSetScoreParent,
} from "../config/ton/tact_NomisScore.js";
import { getHttpEndpoint } from "@orbs-network/ton-access";
import { createContentCellFromURL } from "../config/ton/common-functions.js";
import { IMintData, IToken, ITransactionSender } from "../types/types.js";

function getTonContract(address: string, scoreName: Score) {
  async function getClient() {
    return new TonClient({
      endpoint: await getHttpEndpoint({
        network: "mainnet",
      }),
    });
  }

  const scoreConfig = scores[scoreName];
  const contractAddress = Address.parse(scoreConfig.contract);
  const nomisContract = new NomisScore(contractAddress);

  const mintToken = async ({
    tonConnect,
    mintPrice,
    referrerReward,
    deadline,
    metadataUrl,
    mintedScore,
    signature,
    nonce,
    referrerAddress,
  }: {
    tonConnect: ITransactionSender;
  } & IMintData): Promise<undefined | Transaction> => {
    if (!address || nonce === undefined) {
      throw new Error("No valid data provided");
    }

    const client = await getClient();

    if (!client) {
      throw new Error("No Ton Client provided");
    }

    let lastTx;
    let sended = false;

    try {
      lastTx = (
        await client
          .getTransactions(Address.parse(address), {
            limit: 1,
          })
          .catch(() => {
            return [];
          })
      )[0];

      const sender = {
        send: async (args: SenderArguments) => {
          await tonConnect.sendTransaction({
            messages: [
              {
                address: args.to.toString(),
                amount: args.value.toString(),
                payload: args.body?.toBoc().toString("base64"),
              },
            ],
            validUntil: deadline * 1000,
          });
        },
      };

      const contract = client.open(
        nomisContract as Contract
      ) as OpenedContract<NomisScore>;

      const cellContent = createContentCellFromURL(metadataUrl);

      const mintData: ScoreData = {
        $$type: "ScoreData",
        price: BigInt(mintPrice),
        score: BigInt(mintedScore),
        user: Address.parse(address),
        ref_amount: referrerAddress ? BigInt(referrerReward) : 0n,
        referrer: referrerAddress ? Address.parse(referrerAddress) : null,
        content: cellContent,
      };

      await contract.send(
        sender,
        { value: BigInt(mintPrice) + toNano("0.12") },
        {
          $$type: "SetScoreParent",
          signature: {
            $$type: "Signature",
            signed_bytes: Buffer.from(JSON.parse(atob(signature))),
            valid_until: BigInt(deadline),
            seqno: BigInt(nonce),
          },
          score_data: mintData,
        }
      );

      sended = true;

      if (!lastTx) {
        throw new Error(
          "We cannot find the transaction hash, please track the status of your transaction in your wallet"
        );
      }
    } catch (error) {
      const errorText = (
        (error as AxiosError).response?.data as { error?: string }
      ).error;

      throw new Error(errorText);
    }

    if (!sended || !lastTx) return;

    return lastTx;
  };

  const getMintingTxParams = async ({
    mintPrice,
    referrerReward,
    deadline,
    metadataUrl,
    mintedScore,
    signature,
    nonce,
    referrerAddress,
  }: IMintData): Promise<SenderArguments> => {
    if (!address || nonce === undefined) {
      throw new Error("No valid data provided");
    }

    const body = createMintingTxMessage({
      deadline,
      metadataUrl,
      mintedScore,
      mintPrice,
      nonce,
      referrerAddress,
      referrerReward,
      signature,
    });

    const value = BigInt(mintPrice) + toNano("0.12");

    return {
      to: contractAddress,
      value,
      body,
    };
  };

  const getToken = async (retryCount = 0): Promise<IToken | undefined> => {
    if (!address || !nomisContract) {
      return;
    }

    const client = await getClient();

    try {
      if (!client) return;

      const contract = client.open(
        nomisContract as Contract
      ) as OpenedContract<NomisScore>;

      const userScoreAddress = await contract.getGetUserScoreAddress(
        Address.parse(address)
      );

      const contractUserScore = new UserScoreProxy(userScoreAddress);
      const userContract = client.open(
        contractUserScore as Contract
      ) as OpenedContract<UserScoreProxy>;

      const itemIndex = await userContract.getScoreItemIndex();
      const itemAddress = await contract.getGetNftAddressByIndex(itemIndex);
      const itemContract = client.open(
        ScoreItem.fromAddress(itemAddress) as Contract
      ) as OpenedContract<ScoreItem>;

      const itemScore = await itemContract.getScore();
      const nftData = await itemContract.getGetNftData();

      if (nftData.owner.toString({ bounceable: false }) !== address)
        throw new Error();

      if (itemScore > 0) {
        const metadataUrl = nftData.content
          .asSlice()
          .skip(8)
          .loadStringRefTail();

        let metadataResponse;

        try {
          metadataResponse = await (
            await fetch(metadataUrl, {
              redirect: "follow",
            })
          ).json();
        } catch (error) {
          console.error(error);
        }

        return {
          name: scoreName,
          score: Number(itemScore) ?? 0,
          updated: metadataResponse
            ? (metadataResponse.attributes[5].value as number)
            : 0,
          tokenId: Number(nftData.index),
          calculationModel: 0,
          chainId: 0,
          owner: address,
          realTokenId: itemAddress.toString(),
        };
      }
    } catch (error) {
      if (retryCount < 2) {
        return await getToken(retryCount + 1);
      } else {
        try {
          const { data } = await axios.get("https://nomis.cc/api/ton/holder", {
            params: {
              address,
            },
          });

          if (data.isHolder) {
            return {
              name: scoreName,
              score: data.score * 100,
              updated: data.updated_s,
              tokenId: data.tokenId,
              calculationModel: 0,
              chainId: 0,
              owner: address,
              realTokenId: null,
            };
          }
        } catch {
          console.error(error);
        }
      }
    }
  };

  const getNewTxHash = async (lastTx: Transaction): Promise<string> => {
    const client = await getClient();

    if (!client) {
      throw new Error("No Ton Client provided");
    }

    return new Promise((resolve, reject) => {
      const transactionIntervalId = setInterval(async () => {
        try {
          const tx = (
            await client.getTransactions(Address.parse(address), {
              limit: 1,
            })
          )[0];

          const txHash = tx.hash().toString("hex");

          if (txHash !== lastTx.hash().toString("hex")) {
            clearInterval(transactionIntervalId);
            resolve(txHash);
          }
        } catch (error) {
          clearInterval(transactionIntervalId);
          reject(error);
        }
      }, 5000);
    });
  };

  const checkMintingTxStatus = async (updated: number, mintedScore: number) => {
    const client = await getClient();

    if (!client) {
      throw new Error("No Ton Client provided");
    }

    return new Promise((resolve, reject) => {
      const tokenIntervalId = setInterval(async () => {
        try {
          const oldTokenUpdated = Number(updated ?? 0);
          const tokenData = await getToken();

          if (tokenData?.score == mintedScore) {
            tokenData?.updated && Number(tokenData.updated) != oldTokenUpdated;

            clearInterval(tokenIntervalId);
            resolve(true);
          }
        } catch (error) {
          clearInterval(tokenIntervalId);
          reject(error);
        }
      }, 5000);
    });
  };

  const createMintingTxMessage = ({
    metadataUrl,
    mintPrice,
    mintedScore,
    referrerAddress,
    referrerReward,
    signature,
    deadline,
    nonce,
  }: {
    mintPrice: number;
    referrerReward: number;
    deadline: number;
    metadataUrl: string;
    mintedScore: number;
    signature: string;
    nonce: number;
    referrerAddress: string | null;
  }) => {
    const cellContent = createContentCellFromURL(metadataUrl);

    const mintData: ScoreData = {
      $$type: "ScoreData",
      price: BigInt(mintPrice),
      score: BigInt(mintedScore),
      user: Address.parse(address),
      ref_amount: referrerAddress ? BigInt(referrerReward) : 0n,
      referrer: referrerAddress ? Address.parse(referrerAddress) : null,
      content: cellContent,
    };

    const message = {
      $$type: "SetScoreParent" as "SetScoreParent",
      signature: {
        $$type: "Signature",
        signed_bytes: Buffer.from(JSON.parse(atob(signature))),
        valid_until: BigInt(deadline),
        seqno: BigInt(nonce),
      } as Signature,
      score_data: mintData,
    };

    const body = beginCell().store(storeSetScoreParent(message)).endCell();

    return body;
  };

  const checkIsHolder = async () => {
    const token = await getToken();

    if (token?.realTokenId) {
      return true;
    }

    return false;
  };

  const getNonce = async () => {
    let retryCount = 0;

    const client = await getClient();

    try {
      if (!nomisContract || !address) return;

      const contract = client.open(
        nomisContract as Contract
      ) as OpenedContract<NomisScore>;

      const userScoreAddress = await contract.getGetUserScoreAddress(
        Address.parse(address)
      );
      const contractUserScore = new UserScoreProxy(userScoreAddress);
      const userContract = client.open(
        contractUserScore as Contract
      ) as OpenedContract<UserScoreProxy>;

      return await userContract?.getSeqno();
    } catch (error) {
      if (
        (error as AxiosError).response &&
        (error as AxiosError).response?.status === 503
      ) {
        retryCount++;
        if (retryCount < 2) {
          await getNonce();
        }
      }
    }
  };

  return {
    mintToken,
    checkIsHolder,
    getNonce,
    getNewTxHash,
    getMintingTxParams,
    checkMintingTxStatus,
    getToken,
  };
}

export default getTonContract;
